/**
 * Where a page used to live, and where it lives now (§5.4.1).
 *
 * M2 moves the product listing off `/shop` — which becomes a merchandising page assembled from
 * editorial sections — and onto `/catalogue`, and retires `/products` altogether. The storefront
 * serves the 301s, but a redirect is only ever a safety net: a sitemap or a product feed that
 * still names the old address spends the crawl budget on a hop and hands the index two URLs for
 * one page, which is precisely the ranking split that flattening these routes was meant to end.
 *
 * So both sides read the same map. The storefront's sitemap handler runs every `loc` the API
 * returns through `renameRoute` before it becomes XML, and the redirect middleware asks
 * `routeRenameFor` what to do with an incoming request. Neither has its own copy of the table.
 *
 * There are two kinds of rule here, and they are separate on purpose. {@link ROUTE_RENAMES} is an
 * exact-path table, for the handful of addresses that moved individually. {@link journalRenameFor}
 * is a prefix shape, for the day M2-P7 moved a whole section — `/journal` to `/blog` — where the
 * set of addresses is unbounded and grows with every article. A table cannot express the second
 * without going stale, and a prefix cannot express the first without swallowing its neighbours.
 */

/**
 * Old listing address → new listing address.
 *
 * `/shop` is in here because *the listing* moved off it, not because the address died: the
 * merchandising page that inherits `/shop` is a different page that happens to reuse it. A feed
 * builder emitting the merchandising page must therefore write `/shop` directly rather than run
 * it through this map — and on the day the API publishes that page as its own sitemap row, its
 * entry comes out of this table.
 */
export const ROUTE_RENAMES: Readonly<Record<string, string>> = Object.freeze({
  '/products': '/catalogue',
  '/shop': '/catalogue',
})

/** Where the magazine used to live. Everything under it moved, so it is matched as a prefix. */
const JOURNAL_ROOT = '/journal'

/**
 * The journal's addresses, rewritten onto the blog's (G5.3.2).
 *
 * This is a branch of its own rather than more rows in {@link ROUTE_RENAMES} because the mapping
 * is a shape, not a list. `/journal/{slug}` is one rule that covers every article ever published,
 * including the ones written after the rename shipped; spelling it as an exact-path table would
 * mean a row per article, added by hand, and a 404 the first time somebody forgot one.
 *
 * The three shapes are ordered by specificity: `topic` is a segment the blog spells `category`, so
 * it has to be recognised before the single-segment rule claims it as an article slug. Anything
 * deeper than the journal ever served — a path somebody invented, or a stale link with a suffix on
 * it — lands on `/blog` rather than being left to 404, because the visitor asked for the magazine
 * and the magazine is still there.
 *
 * The slug is carried across as the exact bytes that arrived. Decoding it to «read» it would
 * re-encode a Persian title on the way out, and the redirect would point at an address the router
 * spells differently from the one in the sitemap.
 *
 * @param path A path with its query already removed and its trailing slash already trimmed.
 */
export function journalRenameFor(path: string): string | null {
  if (path !== JOURNAL_ROOT && !path.startsWith(`${JOURNAL_ROOT}/`)) return null

  const rest = path.slice(JOURNAL_ROOT.length).replace(/^\//, '')

  if (rest === '') return '/blog'

  const segments = rest.split('/')

  // `/journal/topic/x` → `/blog/category/x`. A bare `/journal/topic` never listed anything, so it
  // goes to the magazine rather than to a category page with no category.
  if (segments[0] === 'topic') {
    const slug = segments[1] ?? ''

    return slug === '' ? '/blog' : `/blog/category/${slug}`
  }

  return segments.length === 1 ? `/blog/${segments[0]}` : '/blog'
}

/**
 * The query parameters that only ever mean «this is the product listing».
 *
 * An allow-list rather than a deny-list of tracking parameters, and deliberately so: `utm_*`,
 * `fbclid`, `gclid` and `ref` are an open-ended family that grows every time marketing adopts a
 * tool, so a deny-list is out of date the moment it is written and starts bouncing bare `/shop`
 * visits — the arrivals the new page exists for — off to the catalogue. The parameters that
 * *move* are a closed set the catalogue itself defines, so that is the list worth keeping.
 */
export const LISTING_PARAMS: readonly string[] = [
  'page',
  'sort',
  'brand',
  'tag',
  'trait',
  'price_min',
  'price_max',
  'in_stock',
  'on_sale',
  'badge',
  'q',
  'tab',
  'category',
  'flag',
]

const LISTING_PARAM_SET: ReadonlySet<string> = new Set(LISTING_PARAMS)

/** Whether one decoded query key belongs to the listing. */
export function isListingParam(key: string): boolean {
  // `attr[برند]`, `attr[حجم]`, `attr[پوست]` — one key per attribute facet, invented by the
  // merchandiser rather than by us, so they can be recognised by shape but never enumerated.
  return LISTING_PARAM_SET.has(key) || key.startsWith('attr[')
}

/**
 * Percent-encodes anything that cannot legally sit in a `Location` header, and nothing else.
 *
 * Nitro hands a middleware a path that `decodeURI` has already been applied to, so a request for
 * `/journal/%D8%A2%D8%A8…` arrives as `/journal/آبرسان` — and `setHeader` rejects it outright:
 * «Invalid character in header content». Every Persian article slug in the shop redirected to a 500
 * because of it, which is a worse outcome than the 404 the rename existed to prevent.
 *
 * The inverse of `decodeURI` would be `encodeURI`, and it is the wrong tool: it also escapes a `%`
 * that survived decoding — `%2F` is reserved, so `decodeURI` leaves it alone — and would turn a
 * slug containing an encoded slash into `%252F`, a different address again. Encoding only the
 * non-ASCII characters restores exactly what was decoded and leaves everything else byte for byte,
 * which
 * is the property the whole rename depends on: the crawler that indexed the old URL and the shopper
 * who shared it have to end up asking for the same new one.
 */
function headerSafe(target: string): string {
  /*
   * Character by character rather than with one `encodeURIComponent`, which would also escape the
   * `/` between the segments and the `?` and `&` that make the rest a query — turning a path into
   * one long opaque string that no server would route.
   */
  let encoded = ''

  for (const character of target) {
    encoded += (character.codePointAt(0) ?? 0) > 127 ? encodeURIComponent(character) : character
  }

  return encoded
}

/** Everything the rename decision is allowed to look at. */
export interface RouteRenameRequest {
  /** The HTTP verb as it arrived. */
  method: string
  /** The path with its query string still attached, e.g. `/shop?sort=low`. */
  url: string
  /** The `Accept` header, if the client sent one. */
  accept?: string | null
  /** The `X-Requested-With` header, if the client sent one. */
  requestedWith?: string | null
}

const REDIRECTABLE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD'])

/**
 * Whether this request is a person navigating rather than code fetching data.
 *
 * The default is «yes», and that direction matters. A browser navigation asks for `text/html`,
 * but `curl`, a link checker and Playwright's request context send a wildcard `Accept` or none
 * at all, and every one of them is following a link that must land on the new address.
 * Only a client that has explicitly said it wants JSON is left alone: turning its 301 into a
 * cross-route response would surface in the caller as a parse error, not as a redirect.
 */
function isDocumentRequest(request: RouteRenameRequest): boolean {
  if ((request.requestedWith ?? '').toLowerCase() === 'xmlhttprequest') return false

  const accept = (request.accept ?? '').toLowerCase()

  return !(accept.includes('application/json') && !accept.includes('text/html'))
}

/** `/shop/` and `/shop` are one route; a stale link must not miss the rename over a slash. */
function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '')

  return trimmed === '' ? '/' : trimmed
}

function carriesListingParams(search: string): boolean {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  for (const key of params.keys()) {
    if (isListingParam(key)) return true
  }

  return false
}

/**
 * The whole redirect decision, as a pure function: given a request, the address it should be sent
 * to, or `null` for «serve it where it asked».
 *
 * It is a function and not a middleware so that the decision table can be pinned by a unit test
 * that exercises the rules themselves rather than a hand-built imitation of an H3 event — the
 * imitation is the part that drifts, and a redirect table that has quietly drifted is a store
 * whose old links 404.
 */
export function routeRenameFor(request: RouteRenameRequest): string | null {
  // A POST to `/products` is somebody's form, and a 301 would drop its body on the floor. Only
  // the two verbs that ask for a representation are safe to move.
  if (!REDIRECTABLE_METHODS.has(request.method.toUpperCase())) return null
  if (!isDocumentRequest(request)) return null

  const separator = request.url.indexOf('?')
  const path = separator === -1 ? request.url : request.url.slice(0, separator)

  /*
   * Verbatim, never re-serialised. `?q=%D8%B3%D8%B1%D9%85` has to reach `/catalogue` byte for
   * byte: round-tripping a Persian query through URLSearchParams re-encodes spaces as `+` and
   * normalises escapes, so the shopper who shared the link and the crawler that indexed it end up
   * asking for two different URLs — and the SWR cache stores both.
   */
  const search = separator === -1 ? '' : request.url.slice(separator)

  const normalized = normalizePath(path)
  // The exact-path table first, then the journal's prefix shape. Neither can claim the other's
  // addresses, so the order is only about reading it: the table is the shorter of the two.
  const target = ROUTE_RENAMES[normalized] ?? journalRenameFor(normalized)

  if (!target) return null

  /*
   * `/shop` keeps its address for the merchandising page, so only a request that is unmistakably
   * the listing follows the rename. Bare `/shop`, and `/shop` carrying nothing but the campaign
   * tags an ad platform appended, are the new page's own traffic.
   */
  if (normalized === '/shop' && !carriesListingParams(search)) return null

  return headerSafe(target + search)
}

/**
 * Rewrites one sitemap or feed URL onto its current address, leaving anything unrenamed alone.
 *
 * Handles the absolute form as well as the bare path because the sitemap sources emit `/brand/x`
 * while a product feed emits `https://roza.test/brand/x`, and a naive `startsWith` over either
 * would also catch `/shopping-guide`.
 */
export function renameRoute(loc: string): string {
  /*
   * `/shop` is exempt, and this is the day the table's own note said would come: the merchandising
   * page is published as its own sitemap row now, so a feed naming it means the page, not the
   * listing that used to live there. Renaming it here would have emitted `/catalogue` twice and
   * left the page that replaced it out of the index entirely.
   *
   * The redirect side keeps the entry: `/shop?sort=low` is still a stale listing URL and still
   * 301s. What changed is that the bare address now belongs to something.
   */
  if (loc === '/shop' || loc.endsWith('://shop') || /^https?:\/\/[^/]+\/shop$/.test(loc)) return loc

  const origin = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i.exec(loc)?.[0] ?? ''
  const rest = loc.slice(origin.length)
  const separator = rest.search(/[?#]/)
  const path = separator === -1 ? rest : rest.slice(0, separator)
  const tail = separator === -1 ? '' : rest.slice(separator)
  const normalized = normalizePath(path)
  /*
   * The journal's prefix rule runs here as well as in the redirect, and that is the whole point of
   * the two halves reading one table: an article the API still lists as `/journal/x` would
   * otherwise go into the sitemap at an address the middleware immediately 301s away from, which
   * spends the crawl budget on a hop and offers the index two URLs for one article.
   */
  const target = ROUTE_RENAMES[normalized] ?? journalRenameFor(normalized)

  return target ? origin + target + tail : loc
}
